import { useCallback, useMemo } from 'react';
import { useGetRoleDefinitions } from '@cbhq/identity-api-hooks/account-management/fetch';

import { useOrganization } from '../../../contexts/OrganizationContext';

import type { MemberRole, MemberRoleMap } from './constants';

// TODO: Should these be internationalized?
export const PRODUCT_DISPLAY_NAMES: Record<string, string> = {
  cdp: 'Dev Platform',
  account_management: 'Workspace',
  shared: 'Business',
};

export const EXCLUDED_PRODUCTS = new Set(['retail']);

/**
 * Constructs a full role ID from product and role IDs.
 * Format: {product_id}/legal_entity:{role_id}
 */
function constructRoleId(productId: string, roleId: string): MemberRole {
  return `${productId}/legal_entity:${roleId}`;
}

export function getProductKey(rawRole: string) {
  const slashIndex = rawRole.indexOf('/');
  return slashIndex === -1 ? '' : rawRole.slice(0, slashIndex);
}

// Returns the default auto-selected role for a product, or an empty array if none exists.
export function getDefaultRoleValues(
  rolesByProduct: Record<string, MemberRoleMap[]>,
  productKey: string,
): MemberRole[] {
  const options = rolesByProduct[productKey];
  return options?.length ? [options[options.length - 1].value] : [];
}

export type UseRolesReturn = {
  roleMap: Record<MemberRole, MemberRoleMap>;
  /** True when the role exists in role definitions for this org (same as roleMap[role] being set). Excluded products (e.g. retail) never appear in roleMap. */
  hasRoleDefinition: (role: string) => boolean;
  filterRoles: (allRoles: MemberRole[]) => MemberRole[];
  roleOptions: MemberRoleMap[];
  rolesByProduct: Record<string, MemberRoleMap[]>;
  supportedRoles: MemberRole[];
  /** True once role definitions have been fetched; false while loading or on error. */
  hasLoadedRoleDefinitions: boolean;
};

export function useRoles(): UseRolesReturn {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.organizationId;

  // organizationId is passed via additionalParams because the generated RoleDefinitionsArgs
  // type does not include it — using additionalParams appends it to the URL query string.
  const roleDefinitionsData = useGetRoleDefinitions(
    {},
    { additionalParams: { organization_id: organizationId } },
  );

  // Build dynamic role map from API response
  const dynamicRoleData = useMemo(() => {
    const roleMap: Record<MemberRole, MemberRoleMap> = {};
    const supportedRoles: MemberRole[] = [];

    // Add roles from backend response, skipping any already present
    if (roleDefinitionsData?.products) {
      roleDefinitionsData.products.forEach((product) => {
        if (!product.id || !product.roles) {
          return;
        }

        if (EXCLUDED_PRODUCTS.has(product.id)) {
          return;
        }

        product.roles.forEach((role) => {
          if (!role.id || !product.id) {
            return;
          }

          const fullRoleId = constructRoleId(product.id, role.id);

          // Skip if already present (e.g. from hardcoded roles)
          if (roleMap[fullRoleId]) {
            return;
          }

          const productLabel = PRODUCT_DISPLAY_NAMES[product.id] ?? product.name ?? product.id;
          const name = role.name ?? role.id;
          const label = `${productLabel}: ${name}`;
          const description = role.description || '';

          roleMap[fullRoleId] = {
            value: fullRoleId,
            label,
            name,
            description,
            productKey: product.id,
          };
          supportedRoles.push(fullRoleId);
        });
      });
    }

    return { roleMap, supportedRoles };
  }, [roleDefinitionsData]);

  const { roleMap, supportedRoles } = dynamicRoleData;

  const hasRoleDefinition = useCallback(
    (role: string) => roleMap[role as MemberRole] !== undefined,
    [roleMap],
  );

  const filterRoles = useCallback(
    (allRoles: MemberRole[]) => {
      return allRoles.filter(hasRoleDefinition).map((role) => roleMap[role]?.value ?? role);
    },
    [roleMap, hasRoleDefinition],
  );

  const roleOptions = useMemo(() => Object.values(roleMap), [roleMap]);

  const rolesByProduct = useMemo(() => {
    const next: Record<string, MemberRoleMap[]> = {};
    for (const option of roleOptions) {
      (next[option.productKey] ??= []).push(option);
    }
    return next;
  }, [roleOptions]);

  const hasLoadedRoleDefinitions = roleDefinitionsData !== undefined;

  return useMemo(
    () => ({
      roleMap,
      hasRoleDefinition,
      filterRoles,
      roleOptions,
      rolesByProduct,
      supportedRoles,
      hasLoadedRoleDefinitions,
    }),
    [
      roleMap,
      hasRoleDefinition,
      filterRoles,
      roleOptions,
      rolesByProduct,
      supportedRoles,
      hasLoadedRoleDefinitions,
    ],
  );
}
